import type { Client, PickerOptions } from 'filestack-js';

import { getIsLikelyFilestackAuthError } from './filestack-upload-error';

export const filestackS3StoreOptions: PickerOptions['storeTo'] = {
  location: 's3',
  region: 'us-east-1',
};

/** Structural subset of the Filestack Client so tests can stub multiupload without casts. */
export type FilestackMultiuploadClient = Pick<Client, 'multiupload'>;

/**
 * Batch pause/resume/cancel handle for a Filestack multiupload flow. The methods are assigned
 * when the upload starts, so they are optional until then.
 */
export interface FilestackUploadControlToken {
  cancel?: () => void;
  pause?: () => void;
  resume?: () => void;
}

/**
 * Progress event emitted by Filestack's uploader. `files` carries per-file progress keyed by an
 * SDK-internal upload id. Keys appear in input order, but entries disappear once a file finishes,
 * so consumers must key off first appearance rather than assuming a stable index per event.
 */
export interface FilestackMultiuploadProgressEvent {
  totalBytes: number;
  totalPercent: number;
  files?: Record<string, { totalBytes: number; totalPercent: number }>;
}

export interface MultiuploadWithAuthRetryOptions {
  files: File[];
  filestackClient: FilestackMultiuploadClient;
  getFreshFilestackClient: () => Promise<FilestackMultiuploadClient>;
  /**
   * Called just before the upload is retried with a fresh client. The SDK mints new internal
   * upload ids per attempt, so callers tracking per-file progress keys must reset that state here.
   */
  onAuthRetry?: () => void;
  onProgress?: (evt: FilestackMultiuploadProgressEvent) => void;
  /** How often the SDK reports progress, in milliseconds. SDK default is 1000. */
  progressInterval?: number;
  /**
   * Start the upload paused. Used when the user requested a pause before this batch reached the
   * SDK (the SDK only exposes pause controls once the upload starts).
   */
  startPaused?: boolean;
  /** Receives batch-level pause/resume/cancel controls once the upload starts. */
  token?: FilestackUploadControlToken;
}

/**
 * Upload via the shared Filestack client, but recover once from expired upload security (e.g.
 * after a slept tab): retry with a freshly minted client when the SDK throws an auth/policy
 * error, or when it resolves with every file failed — an expired policy fails all files
 * uniformly, and multiupload reports per-file failures through `status` instead of rejecting.
 */
export async function multiuploadWithAuthRetry(
  options: MultiuploadWithAuthRetryOptions
): Promise<unknown[]> {
  const {
    files,
    filestackClient,
    getFreshFilestackClient,
    onAuthRetry,
    onProgress,
    progressInterval,
    startPaused = false,
    token,
  } = options;
  const callerToken = bindCallerToken(token, startPaused);

  function runMultiupload(client: FilestackMultiuploadClient): Promise<MultiuploadAttempt> {
    // Start from a settled promise so a synchronous throw also lands in the rejection branch.
    return Promise.resolve()
      .then(() => {
        const attempt = client.multiupload(
          files,
          { onProgress, progressInterval },
          filestackS3StoreOptions,
          callerToken.createAttemptToken()
        );
        /**
         * multiupload binds the attempt token's controls synchronously, so a pause requested
         * before this attempt started can be applied now.
         */
        callerToken.applyPendingPause();
        return attempt;
      })
      .then(
        (results: unknown[]): MultiuploadAttempt => ({ ok: true, results }),
        (error: unknown): MultiuploadAttempt => ({ error, ok: false })
      );
  }

  const firstAttempt = await runMultiupload(filestackClient);

  /**
   * A cancelled batch resolves with every file failed (and any error it throws is the user's
   * doing), so cancellation never warrants a retry. Otherwise retry both failure shapes an
   * expired policy can produce: a thrown auth error, or an all-failed resolution.
   */
  const shouldRetryWithFreshClient =
    !callerToken.getCancelRequested() &&
    (firstAttempt.ok
      ? getDidAllFilesFail(firstAttempt.results)
      : getIsLikelyFilestackAuthError(firstAttempt.error));
  if (!shouldRetryWithFreshClient) return unwrapAttempt(firstAttempt);

  const freshClient = await getFreshFilestackClient().catch(() => null);
  if (!freshClient) return unwrapAttempt(firstAttempt);

  onAuthRetry?.();
  return unwrapAttempt(await runMultiupload(freshClient));
}

// Matches the SDK's FileState.FAILED status on resolved multiupload file objects.
const FILESTACK_FAILED_STATUS = 'Failed';

export function getIsFailedMultiuploadFile(file: unknown): boolean {
  if (typeof file !== 'object' || file === null || !('status' in file)) return false;
  return file.status === FILESTACK_FAILED_STATUS;
}

type MultiuploadAttempt = { ok: true; results: unknown[] } | { error: unknown; ok: false };

function unwrapAttempt(attempt: MultiuploadAttempt): unknown[] {
  if (!attempt.ok) throw attempt.error;
  return attempt.results;
}

function getDidAllFilesFail(results: unknown[]): boolean {
  return results.length > 0 && results.every(getIsFailedMultiuploadFile);
}

/**
 * The SDK assigns pause/resume/cancel onto whatever token it receives at multiupload call time,
 * overwriting any existing methods. Bind stable delegating methods onto the caller's token that
 * always target the latest attempt's private token, so a retry can't sever the caller's
 * controls. Also track whether cancel was requested (so callers can tell a user-cancelled batch
 * apart from an auth failure) and whether a pause is in effect (so a pause requested before an
 * attempt starts — including the auth-retry attempt — is applied once the SDK exposes controls).
 */
function bindCallerToken(token: FilestackUploadControlToken | undefined, startPaused: boolean) {
  let cancelRequested = false;
  let pauseRequested = startPaused;
  let sdkToken: FilestackUploadControlToken = {};

  if (token) {
    token.pause = () => {
      pauseRequested = true;
      sdkToken.pause?.();
    };
    token.resume = () => {
      pauseRequested = false;
      sdkToken.resume?.();
    };
    token.cancel = () => {
      cancelRequested = true;
      sdkToken.cancel?.();
    };
  }

  return {
    applyPendingPause: () => {
      if (pauseRequested) sdkToken.pause?.();
    },
    createAttemptToken: () => {
      sdkToken = {};
      return sdkToken;
    },
    getCancelRequested: () => cancelRequested,
  };
}
